import { cva, type VariantProps } from "class-variance-authority";
import { Slot } from "radix-ui";
import * as React from "react";

import { cn } from "@/lib/utils";

const badgeVariants = cva(
  "inline-flex w-fit shrink-0 items-center justify-center gap-1 overflow-hidden whitespace-nowrap rounded-full border border-transparent px-[0.5em] py-px font-normal text-xs transition-[color,box-shadow] focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50 aria-invalid:border-destructive aria-invalid:ring-destructive/20 [&>svg]:pointer-events-none [&>svg]:size-[1em]",
  {
    variants: {
      variant: {
        default: "bg-info-200 text-info-800 [a&]:hover:bg-info-300",
        secondary: "bg-neutral-alpha-100 [a&]:hover:bg-neutral-alpha-200",
        outline: "border-border [a&]:hover:bg-neutral-alpha-100",
        destructive: "bg-danger-200 text-danger-800 [a&]:hover:bg-danger-300",
        warning: "bg-warning-200 text-warning-800 [a&]:hover:bg-warning-300",
        success: "bg-success-200 text-success-800 [a&]:hover:bg-success-300",
        ghost: "[a&]:hover:bg-accent [a&]:hover:text-accent-foreground",
        link: "text-primary underline-offset-4 [a&]:hover:underline",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  },
);

function Badge({
  className,
  variant = "default",
  asChild = false,
  ...props
}: React.ComponentProps<"span"> &
  VariantProps<typeof badgeVariants> & { asChild?: boolean }) {
  const Comp = asChild ? Slot.Root : "span";

  return (
    <Comp
      data-slot="badge"
      data-variant={variant}
      className={cn(badgeVariants({ variant }), className)}
      {...props}
    />
  );
}

export { Badge, badgeVariants };
